import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API_BASE, formatApiError } from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowLeft, FileText, AlertTriangle, Users, Wallet, HeartPulse, GraduationCap, Gavel } from "lucide-react";
import { formatCLP } from "@/lib/format";
import { toast } from "sonner";
import IncomeAdjust, { IncomeBreakdown } from "@/components/IncomeAdjust";
import MoneyInput from "@/components/MoneyInput";
import { CategoryEditor, ReopenButton } from "@/components/FichaCaseTools";

function age(b) { if (!b) return null; const d = new Date(b), t = new Date(); let a = t.getFullYear() - d.getFullYear(); if (t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate())) a--; return isNaN(a) ? null : a; }

// Áreas de gastos extraordinarios (deben coincidir con el wizard StepExpenses).
const DECISION_LABEL = { approve: "Aprobada", propose: "Propuesta de %", reject: "Rechazada", request_info: "Solicitó información" };

const EXPENSE_AREAS = [
  ["medicos", "Gastos Médicos Extraordinarios"],
  ["necesidades_especiales", "Necesidades especiales / discapacidad"],
  ["vivienda_adicional", "Vivienda adicional (familias separadas/divorciadas)"],
  ["apoyo_familiares", "Apoyo a familiares en necesidad"],
  ["otros", "Otros gastos extraordinarios recurrentes"],
];
const LEGACY_AREA = {
  "Gastos médicos recurrentes": "medicos", "Medicamentos permanentes": "medicos", "Tratamientos": "medicos",
  "Cuidado de familiares": "apoyo_familiares", "Apoyo económico a familiares dependientes": "apoyo_familiares",
  "Otra obligación familiar extraordinaria": "otros",
};
const AREA_KEYS = new Set(EXPENSE_AREAS.map(([k]) => k));
const resolveArea = (e) => (e.area && AREA_KEYS.has(e.area)) ? e.area : (LEGACY_AREA[e.category] || "otros");

// Palabras que sugieren un gasto NORMAL (no extraordinario) para alertar a la comisión.
const NORMAL_HINTS = [
  "dividendo", "hipotec", "arriendo", "colegio", "colegiatura", "matricula", "matrícula",
  "mensualidad", "educacion", "educación", "universidad", "seguro", "isapre", "aliment",
  "comida", "supermercado", "luz", "agua", "gas", "internet", "telefono", "teléfono",
  "bencina", "combustible", "transporte", "vehiculo", "vehículo", "auto", "vacacion",
  "vacación", "viaje", "credito de consumo", "crédito de consumo", "tarjeta",
];
const looksNormal = (e) => {
  const t = `${e.description || ""} ${e.category || ""}`.toLowerCase();
  return NORMAL_HINTS.some((w) => t.includes(w));
};

function Card({ title, icon: Icon, children }) {
  return (
    <section className="bg-white rounded-2xl border border-border p-6">
      <h2 className="flex items-center gap-2 text-lg font-semibold mb-4">{Icon && <Icon className="w-5 h-5 text-accent" />}{title}</h2>
      {children}
    </section>
  );
}

export default function AdminFicha() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [decision, setDecision] = useState("approve");
  const [percentage, setPercentage] = useState("");
  const [comment, setComment] = useState("");
  const [internalComment, setInternalComment] = useState("");
  const [ihPct, setIhPct] = useState("");
  const [busy, setBusy] = useState(false);
  const [expItems, setExpItems] = useState({});
  const [expOverride, setExpOverride] = useState("");
  const [expNote, setExpNote] = useState("");

  const load = async () => {
    try { const { data } = await api.get(`/admin/applications/${id}`); setData(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); navigate("/admin"); }
  };
  useEffect(() => { load(); window.scrollTo(0, 0); }, [id]);

  // Inicializa ajuste de gastos y precarga la explicación estándar cuando la beca es menor a lo solicitado.
  useEffect(() => {
    if (!data) return;
    const app = data.application;
    const calc = data.calculation || {};
    const review = app.expenses_review || {};
    setExpItems(review.items || {});
    setExpOverride(review.override_total != null ? String(review.override_total) : "");
    setExpNote(review.note || "");
    const decisionState = !["processed", "quarantined", "pending_access_validation"].includes(app.status);
    const lower = calc.recommended_pct != null && app.requested_pct != null && calc.recommended_pct < app.requested_pct;
    if (decisionState && lower && !comment) setComment(data.diff_explanation || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const openDoc = async (docId) => {
    try {
      const token = localStorage.getItem("eim_token");
      const resp = await fetch(`${API_BASE}/documents/${docId}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("No se pudo abrir el documento."); }
  };

  const submitDecision = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/applications/${id}/decision`, {
        decision, percentage: decision === "propose" && percentage !== "" ? Number(percentage) : null,
        comment: decision === "approve" ? null : comment, internal_comment: internalComment.trim() || null,
      });
      toast.success("Decisión registrada.");
      setComment(""); setInternalComment(""); setPercentage("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const adjustExpenses = async () => {
    setBusy(true);
    try {
      const app = data.application;
      const items = {};
      (app.expenses || []).forEach((e, i) => {
        const key = String(e.id ?? i);
        if (expItems[key] !== undefined && expItems[key] !== "" && expItems[key] !== null)
          items[key] = Number(expItems[key]);
      });
      await api.post(`/admin/applications/${id}/adjust-expenses`, {
        items: Object.keys(items).length ? items : null,
        override_total: expOverride !== "" ? Number(expOverride) : null,
        note: expNote || null,
      });
      toast.success("Gastos aceptados ajustados y beca recalculada.");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const submitIh = async () => {
    if (ihPct === "") return;
    setBusy(true);
    try { await api.post(`/admin/applications/${id}/ih-check`, { percentage: Number(ihPct) }); toast.success("Beca IH registrada y recálculo aplicado."); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const entryDecision = async (approve) => {
    if (!approve && !comment.trim()) { toast.error("Indica el motivo del rechazo."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/applications/${id}/entry-decision`, { approve, comment: comment.trim() || null });
      toast.success(approve ? "Ingreso validado. La solicitud pasa a evaluación." : "Ingreso rechazado. La solicitud queda en cuarentena.");
      setComment(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const sendIhRequest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/applications/${id}/ih-request`);
      if (data?.email_conflict) {
        toast.error("El correo de la asistente social ya está registrado como otra cuenta (p. ej. un socio). Configura un correo exclusivo para la asistente social en Configuración.");
      } else if (data?.email_sent === false) {
        toast.warning("Caso registrado, pero no se pudo enviar el correo a la asistente social. Revisa la configuración de correo.");
      } else {
        toast.success("Consulta enviada a la asistente social del Instituto Hebreo.");
      }
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!data) return <AdminLayout><div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></AdminLayout>;

  const app = data.application;
  const calc = data.calculation || {};
  const fam = app.family || {};
  const requested = app.accept_committee_pct ? "El que determine EIM" : (app.requested_pct != null ? `${app.requested_pct}%` : "—");
  const canAdjust = !["processed", "quarantined"].includes(app.status);

  return (
    <AdminLayout>
      <Button variant="ghost" onClick={() => navigate("/admin")} className="mb-4"><ArrowLeft className="w-4 h-4 mr-1" /> Volver</Button>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">{app.human_id} · {fam.titular?.nombre}</h1>
          <p className="text-muted-foreground">{fam.titular?.rut} · {fam.titular?.categoria || "—"}</p>
        </div>
        <div className="flex items-center gap-3">
          <ReopenButton appId={id} status={app.status} onSaved={load} />
          <StatusBadge status={app.public_status} />
        </div>
      </div>

      {/* Executive summary */}
      <div className="bg-primary text-white rounded-2xl p-6 mb-6 grid sm:grid-cols-3 gap-6">
        <div><p className="text-white/60 text-xs uppercase tracking-wide">Solicita</p><p className="text-3xl font-black">{requested}</p></div>
        <div><p className="text-white/60 text-xs uppercase tracking-wide">Sistema recomienda</p><p className="text-3xl font-black text-accent" data-testid="ficha-recommended">{calc.recommended_pct != null ? `${calc.recommended_pct}%` : "—"}</p></div>
        <div><p className="text-white/60 text-xs uppercase tracking-wide">IAJAD</p><p className="text-3xl font-black">{calc.iajad ? "Sí" : "No"}</p></div>
        <div className="sm:col-span-3 border-t border-white/15 pt-4 flex items-center gap-2">
          <span className="text-white/60 text-xs uppercase tracking-wide">Categoría</span>
          <CategoryEditor appId={id} current={fam.titular?.categoria} canEdit={canAdjust} onSaved={load} />
        </div>
        <div className="sm:col-span-3 border-t border-white/15 pt-4">
          <p className="text-white/60 text-xs uppercase tracking-wide mb-1">Motivo de recomendación</p>
          <p className="text-sm text-white/90" data-testid="ficha-rule">{calc.explanation?.rule} {calc.table_version ? `Versión tabla: ${calc.table_version}.` : ""}</p>
        </div>
        <div className="sm:col-span-3 border-t border-white/15 pt-4">
          <p className="text-white/60 text-xs uppercase tracking-wide mb-2">Desglose del cálculo</p>
          <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="ficha-calc-breakdown">
            <span className="bg-white/10 rounded-full px-3 py-1">% tabla: <b>{calc.table_pct != null ? `${calc.table_pct}%` : "—"}</b></span>
            {calc.ih_scholarship_pct ? (
              <span className="bg-white/10 rounded-full px-3 py-1">% beca IH: <b>{calc.ih_scholarship_pct}%</b></span>
            ) : null}
            <span className="bg-white/10 rounded-full px-3 py-1">% solicitado: <b>{requested}</b></span>
            <span className="text-white/40">→</span>
            <span className="bg-accent/20 text-accent rounded-full px-3 py-1 font-bold">Recomendado: {calc.recommended_pct != null ? `${calc.recommended_pct}%` : "—"}</span>
          </div>
          {calc.explanation?.capped_by_request && (
            <p className="text-xs text-amber-300 mt-2" data-testid="ficha-capped-note">
              La beca de tabla ({calc.table_pct}%) supera lo solicitado por la familia ({app.requested_pct}%); se recomienda lo solicitado.
            </p>
          )}
          {calc.explanation?.floored_by_ih && (
            <p className="text-xs text-emerald-300 mt-2" data-testid="ficha-ihfloor-note">
              Se recomienda al menos la beca confirmada por el colegio IH ({calc.ih_scholarship_pct}%).
            </p>
          )}
        </div>
        {(data.beca_last3 || []).length > 0 && (
          <div className="sm:col-span-3 border-t border-white/15 pt-4">
            <p className="text-white/60 text-xs uppercase tracking-wide mb-2">Beca en años anteriores</p>
            <div className="flex flex-wrap gap-2 text-sm" data-testid="ficha-beca-last3">
              {data.beca_last3.map((b) => (
                <span key={b.year} className={`rounded-full px-3 py-1 ${b.had_beca ? "bg-accent/20 text-accent" : "bg-white/10 text-white/70"}`} data-testid={`beca-year-${b.year}`}>
                  {b.year}: <b>{b.had_beca ? `Sí · ${b.percentage}%` : "No"}</b>
                </span>
              ))}
            </div>
          </div>
        )}
        {app.need_explanation && (
          <div className="sm:col-span-3 border-t border-white/15 pt-4">
            <p className="text-white/60 text-xs uppercase tracking-wide mb-1">Por qué la familia solicita la beca</p>
            <p className="text-sm text-white/90 whitespace-pre-wrap" data-testid="ficha-need-explanation">{app.need_explanation}</p>
          </div>
        )}
      </div>

      {data.not_in_base && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-5 mb-6" data-testid="ficha-notinbase-banner">
          <p className="flex items-center gap-2 font-semibold text-amber-900 mb-1"><AlertTriangle className="w-5 h-5" /> Postulante fuera de la base de socios EIM</p>
          <p className="text-sm text-amber-900">El RUT del titular <strong>no figura</strong> en la base de datos de socios del EIM. Verificar manualmente la condición de socio antes de resolver.</p>
        </div>
      )}

      {(calc.alerts?.length > 0 || calc.manual_review) && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-6">
          <p className="flex items-center gap-2 font-semibold text-amber-900 mb-2"><AlertTriangle className="w-5 h-5" /> Alertas e inconsistencias</p>
          <ul className="text-sm text-amber-900 list-disc pl-5 space-y-1">
            {calc.manual_review && <li>Evaluación manual requerida (documentación faltante).</li>}
            {(calc.alerts || []).map((a, i) => <li key={i}>{a.msg}</li>)}
          </ul>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Familia" icon={Users}>
          <ul className="text-sm space-y-1">
            <li><strong>{fam.titular?.nombre}</strong> · {fam.titular?.rut} {age(fam.titular?.birthdate) != null ? `· ${age(fam.titular?.birthdate)} años` : ""}</li>
            {fam.spouse?.nombre && <li>{fam.spouse.nombre} · {fam.spouse.rut} (cónyuge)</li>}
            {(fam.children || []).map((c, i) => <li key={i}>{[c.nombre, c.rut, c.age != null ? `${c.age} años` : null, c.colegio].filter(Boolean).join(" · ")}{c.age != null && c.age >= 25 && <span className="ml-2 text-[11px] font-semibold rounded-full bg-amber-100 text-amber-900 px-2 py-0.5" data-testid={`ficha-child-over25-${i}`}>no cuenta para el grupo</span>}</li>)}
          </ul>
          <p className="text-xs text-muted-foreground mt-3" data-testid="ficha-household-size">Tamaño real: {calc.household_size_actual} · Para beneficio: {calc.household_size_for_benefit}</p>
        </Card>

        <Card title="Situación económica" icon={Wallet}>
          <div className="text-sm space-y-2">
            {(app.income?.adults || []).length > 0 && <IncomeBreakdown app={app} />}
            <div className="flex justify-between border-t border-border pt-2"><span>Ingreso bruto familiar</span><strong>{formatCLP(calc.family_gross_income)}</strong></div>
            <div className="flex justify-between"><span>Gastos aceptados</span><span>− {formatCLP(calc.accepted_expenses)}</span></div>
            <div className="flex justify-between text-base"><span>Ingreso ajustado</span><strong className="text-primary">{formatCLP(calc.adjusted_income)}</strong></div>
          </div>
          <IncomeAdjust appId={id} app={app} canAdjust={canAdjust} onSaved={load} />
        </Card>

        <Card title="Antecedentes extraordinarios" icon={HeartPulse}>
          {(app.expenses || []).length === 0 ? <p className="text-sm text-muted-foreground">Sin gastos declarados.</p> : (
            <>
              <p className="text-xs text-muted-foreground mb-3">
                La tabla ya contempla los gastos familiares esperables (vivienda, educación, salud, seguros).
                Ajusta el <strong>monto aceptado</strong> de cada ítem para considerar sólo el gasto extraordinario real.
                {calc.family_gross_income ? <> Referencia: un segundo arriendo (incl. gastos comunes) no suele superar el <strong>20% del ingreso mensual</strong> ≈ {formatCLP(Math.round(0.2 * calc.family_gross_income))}.</> : null}
              </p>
              <div className="space-y-4" data-testid="expenses-adjust-list">
                {EXPENSE_AREAS.map(([areaKey, areaTitle]) => {
                  const items = (app.expenses || []).map((e, i) => ({ e, i })).filter(({ e }) => resolveArea(e) === areaKey);
                  if (items.length === 0) return null;
                  return (
                    <div key={areaKey} data-testid={`ficha-expense-area-${areaKey}`}>
                      <p className="text-xs font-semibold uppercase text-slate-500 mb-1">{areaTitle}</p>
                      <div className="space-y-2">
                        {items.map(({ e, i }) => {
                          const key = String(e.id ?? i);
                          const val = expItems[key] !== undefined ? expItems[key] : (e.amount ?? "");
                          return (
                            <div key={key} className="grid grid-cols-5 gap-2 items-center text-sm">
                              <span className="col-span-3 flex items-center gap-1.5">
                                <span>{e.description || e.category}{e.related_person ? ` · ${e.related_person}` : ""}</span>
                                {looksNormal(e) && (
                                  <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-100 rounded-full px-2 py-0.5 shrink-0"
                                    title="Parece un gasto familiar normal (no extraordinario). Revísalo: podría no corresponder."
                                    data-testid={`expense-normal-flag-${i}`}>
                                    <AlertTriangle className="w-3 h-3" /> posible gasto normal
                                  </span>
                                )}
                              </span>
                              <span className="text-right text-muted-foreground">{formatCLP(e.amount)}</span>
                              <MoneyInput className="h-9 rounded-lg text-right" value={val === "" || val == null ? null : Number(val)}
                                onChange={(v) => setExpItems({ ...expItems, [key]: v == null ? "" : String(v) })}
                                disabled={!canAdjust || expOverride !== ""}
                                data-testid={`expense-accepted-${i}`} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 grid grid-cols-5 gap-2 items-center">
                <Label className="col-span-3 text-xs font-semibold uppercase text-slate-500">Override total (opcional)</Label>
                <span className="text-right text-xs text-muted-foreground">reemplaza</span>
                <MoneyInput className="h-9 rounded-lg text-right" value={expOverride === "" ? null : Number(expOverride)}
                  onChange={(v) => setExpOverride(v == null ? "" : String(v))} placeholder="—" disabled={!canAdjust}
                  data-testid="expense-override-total" />
              </div>
              <Textarea className="rounded-xl mt-3" placeholder="Nota / justificación del ajuste (opcional)"
                value={expNote} onChange={(ev) => setExpNote(ev.target.value)} disabled={!canAdjust} data-testid="expense-adjust-note" />
              <div className="flex items-center justify-between mt-3">
                <span className="text-sm">Gastos aceptados actuales: <strong>{formatCLP(calc.accepted_expenses)}</strong></span>
                {canAdjust && (
                  <Button onClick={adjustExpenses} disabled={busy} className="btn-lift h-10 rounded-xl px-5" data-testid="btn-adjust-expenses">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar ajuste y recalcular"}
                  </Button>
                )}
              </div>
              {app.expenses_review?.by_name && (
                <p className="text-xs text-muted-foreground mt-2" data-testid="expense-review-meta">
                  Último ajuste por {app.expenses_review.by_name} · {new Date(app.expenses_review.at).toLocaleDateString("es-CL")}
                  {app.expenses_review.note ? ` · ${app.expenses_review.note}` : ""}
                </p>
              )}
            </>
          )}
        </Card>

        <Card title="Instituto Hebreo / IAJAD" icon={GraduationCap}>
          <p className="text-sm mb-2">IAJAD: <strong>{calc.iajad ? "Sí" : "No"}</strong></p>
          {app.family?.ih_beca && (
            <div className="mb-3">
              <p className="text-sm">Beca IH declarada: <strong>Sí{app.family.ih_beca_pct != null && app.family.ih_beca_pct !== "" ? ` (${app.family.ih_beca_pct}%)` : ""}</strong></p>
              {app.family.ih_beca_consent ? (
                <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 mt-1.5" data-testid="ficha-ih-consent">
                  <span className="font-semibold">Autorización del socio</span>{data.ih_check?.consent_at ? ` · ${new Date(data.ih_check.consent_at).toLocaleString("es-CL")}` : ""}
                  {data.ih_check?.consent_text && <p className="mt-0.5 text-emerald-900/80">“{data.ih_check.consent_text}”</p>}
                </div>
              ) : (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-1.5" data-testid="ficha-ih-noconsent">El socio NO autorizó la ratificación: la beca del IH no se considera en esta solicitud.</p>
              )}
            </div>
          )}
          {(calc.ih_children || []).length > 0 ? (
            <ul className="text-sm space-y-1 mb-4">{calc.ih_children.map((c, i) => <li key={i}>{c.nombre} · {c.age} años · {c.condition} {c.eligible ? "(elegible)" : "(no elegible)"}</li>)}</ul>
          ) : <p className="text-sm text-muted-foreground mb-4">Sin hijos registrados en IH.</p>}
          {calc.iajad && (() => {
            const ihc = data.ih_check || {};
            const answered = ihc.status === "responded";
            const working = ihc.status === "in_review";
            const canRequest = !!(app.family?.ih_beca && app.family?.ih_beca_consent);
            const declared = app.family?.ih_beca_pct;
            const differs = answered && declared != null && declared !== "" && Number(declared) !== Number(ihc.percentage);
            return (
              <div className="mb-4">
                {answered ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-3 text-sm" data-testid="ih-confirmed">
                    <p className="font-semibold text-emerald-800">
                      {ihc.percentage != null
                        ? `La asistente social confirmó que la familia tiene un ${ihc.percentage}% de beca en el IH.`
                        : "La asistente social respondió la consulta de beca IH."}
                    </p>
                    {ihc.responded_at && <p className="text-xs text-emerald-700 mt-0.5">Respondido el {new Date(ihc.responded_at).toLocaleString("es-CL")}</p>}
                    {differs && <p className="text-xs text-amber-700 mt-1" data-testid="ih-declared-differs">El socio había declarado {declared}%; se usará el {ihc.percentage}% confirmado por la asistente social.</p>}
                    {ihc.note && <p className="text-xs text-emerald-900/80 mt-1">Nota de la asistente social: {ihc.note}</p>}
                  </div>
                ) : (
                  <>
                    <Button onClick={sendIhRequest} disabled={busy || !canRequest} variant="outline" className="rounded-xl border-2 w-full" data-testid="btn-ih-request">
                      Enviar consulta a asistente social IH
                    </Button>
                    {!canRequest && <p className="text-xs text-amber-700 mt-2">Disponible solo si el socio declaró beca IH y autorizó la ratificación.</p>}
                    {(ihc.status === "requested" || working) && <p className="text-xs text-amber-700 mt-2" data-testid="ih-awaiting">Consulta {working ? "en revisión por" : "enviada a"} {ihc.requested_to || "la asistente social"}. A la espera de respuesta.</p>}
                  </>
                )}
              </div>
            );
          })()}
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs font-semibold uppercase text-slate-500">Beca IH (%) · ajuste manual</Label>
              <Input type="number" className="h-11 rounded-xl mt-1" value={ihPct} placeholder={data.ih_check?.percentage != null ? String(data.ih_check.percentage) : "—"} onChange={(e) => setIhPct(e.target.value)} data-testid="ih-pct-input" />
            </div>
            <Button onClick={submitIh} disabled={busy} className="rounded-xl h-11" data-testid="btn-record-ih">Registrar</Button>
          </div>
          {data.ih_check?.status !== "responded" && data.ih_check?.percentage != null && <p className="text-xs text-muted-foreground mt-2">Registrado: {data.ih_check.percentage}%</p>}
        </Card>
      </div>

      {(data.info_requests || []).length > 0 && (
        <div className="my-6">
        <Card title="Información adicional solicitada">
          <ul className="space-y-3 text-sm" data-testid="ficha-info-requests">
            {data.info_requests.map((r) => {
              const resp = (data.info_responses || []).filter((x) => x.request_id === r.id);
              const docs = data.documents.filter((d) => d.created_at > r.created_at && d.scope === "additional");
              return (
                <li key={r.id} className="border border-border rounded-xl p-3">
                  <p><strong>Pedido ({new Date(r.created_at).toLocaleDateString("es-CL")}):</strong> {r.message}</p>
                  {resp.map((x) => <p key={x.id} className="mt-1 text-emerald-800"><strong>Respuesta del socio ({new Date(x.at).toLocaleDateString("es-CL")}):</strong> {x.message}</p>)}
                  <p className="mt-1 text-xs text-muted-foreground">{r.resolved ? "Respondida" : "Pendiente de respuesta"} · {docs.length} documento(s) adicional(es) adjunto(s)</p>
                </li>
              );
            })}
          </ul>
        </Card>
        </div>
      )}

      <Card title="Documentos" >
        {data.documents.length === 0 ? <p className="text-sm text-muted-foreground">Sin documentos (pueden haber sido eliminados tras finalizar el proceso).</p> : (
          <div className="grid sm:grid-cols-2 gap-3">
            {data.documents.map((d) => (
              <button key={d.id} onClick={() => openDoc(d.id)} className="flex items-center gap-2 border border-border rounded-xl p-3 hover:bg-secondary/40 text-left" data-testid={`doc-${d.id}`}>
                <FileText className="w-4 h-4 text-primary shrink-0" />
                <div className="min-w-0"><p className="text-sm font-medium truncate">{d.original_filename}</p><p className="text-xs text-muted-foreground">{d.doc_type}</p></div>
              </button>
            ))}
          </div>
        )}
      </Card>

      {(data.beca_history || []).length > 0 && (
        <Card title="Historial de becas" icon={GraduationCap}>
          <p className="text-xs text-muted-foreground mb-3">Bases históricas (2016–2025) y decisiones de años anteriores registradas en EIM Becas, por RUT, nombre o email del titular.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ficha-beca-history">
              <thead>
                <tr className="text-left text-xs uppercase text-slate-500 border-b border-border">
                  <th className="py-2 pr-4">Año</th>
                  <th className="py-2 pr-4">% Beca</th>
                  <th className="py-2 pr-4">Tipo socio</th>
                  <th className="py-2 pr-4">Observaciones</th>
                </tr>
              </thead>
              <tbody>
                {data.beca_history.map((h, i) => (
                  <tr key={i} className="border-b border-border/60" data-testid={`beca-hist-row-${i}`}>
                    <td className="py-2 pr-4 font-semibold text-primary">{h.year}</td>
                    <td className="py-2 pr-4">{h.percentage != null ? `${h.percentage}%` : "—"}</td>
                    <td className="py-2 pr-4">{h.tipo_socio || "—"}</td>
                    <td className="py-2 pr-4 text-muted-foreground">{h.observaciones || "—"}{h.application_id && (
                      <button type="button" onClick={() => navigate(`/admin/solicitud/${h.application_id}`)} className="ml-2 text-primary underline text-xs" data-testid={`beca-hist-open-${i}`}>Ver ficha</button>
                    )}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="mt-6" />
      <Card title="Decisión del comité" icon={Gavel}>
        {app.status === "pending_access_validation" ? (
          <div className="space-y-3" data-testid="entry-validation-box">
            {(app.recommenders?.length > 0) ? (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-testid="recommenders-box">
                <p className="flex items-center gap-2 font-semibold text-blue-900 mb-2"><Users className="w-4 h-4" /> Socios recomendantes (2ª validación)</p>
                <ul className="text-sm text-blue-900 space-y-2">
                  {app.recommenders.map((r, i) => {
                    const conf = (data.recommender_confirmations || []).find((c) => c.index === i);
                    const st = conf?.status === "confirmed" ? { t: "Confirmó", cls: "bg-emerald-100 text-emerald-800" }
                      : conf?.status === "declined" ? { t: "No recomendó", cls: "bg-slate-200 text-slate-700" }
                      : { t: "Pendiente", cls: "bg-amber-100 text-amber-800" };
                    return (
                      <li key={i} data-testid={`recommender-${i}`} className="border-b border-blue-100 last:border-0 pb-2 last:pb-0">
                        <div className="flex items-center justify-between gap-2">
                          <span><strong>{r.nombre}</strong> · {r.rut} · {r.email}</span>
                          <span className={`text-xs font-semibold rounded-full px-2 py-0.5 whitespace-nowrap ${st.cls}`} data-testid={`recommender-status-${i}`}>{st.t}</span>
                        </div>
                        {conf?.relationship && <p className="text-xs text-blue-800 mt-1 italic">“{conf.relationship}”</p>}
                        {conf?.relationship_hidden && <p className="text-xs text-red-700 mt-1 italic">Comentario retenido por moderación de contenido.</p>}
                      </li>
                    );
                  })}
                </ul>
                <p className="text-xs text-blue-700 mt-3">Solo se autoriza a personas de la comunidad judía. Las recomendaciones son un antecedente para la decisión de atención a socios.</p>
              </div>
            ) : (
              <p className="text-sm bg-amber-50 border border-amber-200 rounded-xl p-3 text-amber-900">
                Postulante <strong>no registrado en la base EIM</strong>. Debes validar su ingreso antes de evaluar.
              </p>
            )}
            <Textarea className="rounded-xl" placeholder="Comentario (opcional, obligatorio si rechazas)" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="entry-comment" />
            <div className="flex gap-3">
              <Button onClick={() => entryDecision(true)} disabled={busy} className="btn-lift h-11 rounded-xl flex-1" data-testid="btn-entry-approve">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Dar OK (validar ingreso)"}
              </Button>
              <Button onClick={() => entryDecision(false)} disabled={busy} variant="outline" className="h-11 rounded-xl flex-1 border-2 border-red-200 text-red-700" data-testid="btn-entry-reject">
                Rechazar ingreso
              </Button>
            </div>
          </div>
        ) : app.status === "quarantined" ? (
          <p className="text-sm bg-red-50 border border-red-200 rounded-xl p-3 text-red-900" data-testid="quarantine-note">
            Solicitud en cuarentena{app.quarantine_reason ? ` (${app.quarantine_reason})` : ""}.
            {app.quarantine_reason === "ingreso_no_aprobado" ? " A la espera de que el postulante ingrese 3 socios recomendantes." : " Sin más acciones disponibles."}
          </p>
        ) : app.status === "processed" ? (
          <p className="text-sm bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-emerald-900">Solicitud procesada. Decisión: {DECISION_LABEL[app.decision_type] || app.decision_type} {app.final_pct != null ? `· ${app.final_pct}%` : ""}.</p>
        ) : (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase text-slate-500">Decisión</Label>
                <Select value={decision} onValueChange={setDecision}>
                  <SelectTrigger className="h-12 rounded-xl mt-1.5" data-testid="decision-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approve">Aprobar</SelectItem>
                    <SelectItem value="propose">Proponer porcentaje diferente</SelectItem>
                    <SelectItem value="reject">Rechazar</SelectItem>
                    <SelectItem value="request_info">Solicitar más información</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {decision === "approve" && (
                <div>
                  <Label className="text-xs font-semibold uppercase text-slate-500">Porcentaje (%)</Label>
                  <Input readOnly className="h-12 rounded-xl mt-1.5 bg-secondary/40 font-bold text-primary cursor-not-allowed" data-testid="decision-approve-pct"
                    value={data.calculation?.recommended_pct != null ? `${Math.floor(Number(data.calculation.recommended_pct) + 0.5)}` : "Sin % sugerido"} />
                  <p className="text-xs text-muted-foreground mt-1">% recomendado por el sistema. Para otro %, usa «Proponer porcentaje diferente».</p>
                </div>
              )}
              {decision === "propose" && (
                <div>
                  <Label className="text-xs font-semibold uppercase text-slate-500">Porcentaje (%)</Label>
                  <Input type="number" className="h-12 rounded-xl mt-1.5" value={percentage} onChange={(e) => setPercentage(e.target.value)} data-testid="decision-pct" />
                </div>
              )}
            </div>
            {decision !== "approve" && (
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase text-slate-500">
                  Mensaje al socio · {decision === "request_info" ? "información solicitada" : decision === "reject" ? "motivo del rechazo" : "fundamento del %"} (obligatorio)
                </Label>
                {decision === "propose" && (
                  <button type="button" onClick={() => setComment(data.diff_explanation || "")}
                    className="text-xs text-primary underline" data-testid="btn-use-standard-explanation">
                    Usar explicación estándar
                  </button>
                )}
              </div>
              <Textarea className="rounded-xl mt-1.5" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="decision-comment" />
              <p className="text-xs text-muted-foreground mt-1">El socio lo recibirá por correo y lo verá en su pantalla.</p>
            </div>
            )}
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Comentario interno (opcional)</Label>
              <Textarea className="rounded-xl mt-1.5" value={internalComment} onChange={(e) => setInternalComment(e.target.value)} data-testid="decision-internal-comment" />
              <p className="text-xs text-muted-foreground mt-1">Solo lo ve el comité (ficha, reportes y correo a becas@eimbecas.cl). Nunca se envía al socio.</p>
            </div>
            <Button onClick={submitDecision} disabled={busy} className="btn-lift h-12 rounded-xl px-8" data-testid="btn-submit-decision">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Registrar decisión"}
            </Button>
          </div>
        )}
        {data.decisions.length > 0 && (
          <div className="mt-6 border-t border-border pt-4">
            <p className="text-xs font-semibold uppercase text-slate-500 mb-2">Historial de decisiones</p>
            <ul className="text-sm space-y-2">
              {data.decisions.map((d, i) => (
                <li key={i} className="flex justify-between"><span>{DECISION_LABEL[d.decision] || d.decision} {d.percentage != null ? `· ${d.percentage}%` : ""}{d.comment ? ` — Socio: ${d.comment}` : ""}{d.internal_comment ? ` — Interno: ${d.internal_comment}` : ""}</span><span className="text-muted-foreground text-xs">{d.by_name} · {new Date(d.created_at).toLocaleDateString("es-CL")}</span></li>
              ))}
            </ul>
          </div>
        )}
      </Card>
    </AdminLayout>
  );
}
